import useDeleteChatbot from '@/hooks/useDeleteChatbot';
import { useUpdateName } from '@/hooks/useUpdateName';
import type { WorkspaceItem } from '@/hooks/useWorkspace';
import { cn } from '@/utils/utils';
import { EllipsisVertical, SquarePen, TrashIcon } from 'lucide-react';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { Button } from '../Button/Button';
import { useChatId } from '../ChatV2/ChatV2Input/hooks/useChatId';
import { useConfirmationStore } from '../Confirmation/ConfirmationStore';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu';
import { Icon } from '../ui/Icon';
import RenameForm from './RenameForm';

interface SidebarChatDropdownProps {
  chat: WorkspaceItem;
  isActive?: boolean;
  onDropdownOpenChange?: (open: boolean) => void;
}

const SidebarChatDropdown = ({
  chat,
  isActive,
  onDropdownOpenChange,
}: SidebarChatDropdownProps) => {
  const [openRename, setOpenRename] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const router = useRouter();
  const { chatId } = useChatId();

  const { mutateDelete } = useDeleteChatbot({
    onMutate: () => {
      setOpenMenu(false);
    },
  });

  const { updateName, isRenaming } = useUpdateName();
  const confirm = useConfirmationStore((state) => state.openConfirmation);

  const isLoading = isRenaming;

  const handleDeleteClick = () => {
    setOpenMenu(false);

    confirm({
      title: 'Delete chat?',
      description:
        'This chat and all its messages will be permanently deleted.',
      actionLabel: 'Delete chat',
      actionButtonVariant: 'destructive',
      onAction: () => {
        mutateDelete([{ id: chat.id }]);

        // If we're currently viewing this chat, redirect to new chat
        if (chatId === chat.id) {
          router.push('/new');
        }
      },
      cancelLabel: 'Cancel',
      onCancel: () => {},
    });
  };

  return (
    <DropdownMenu
      modal={true}
      open={openMenu}
      onOpenChange={(open) => {
        setOpenMenu(open);
        onDropdownOpenChange?.(open);

        setTimeout(() => {
          setOpenRename(false);
        }, 100);
      }}
    >
      <DropdownMenuTrigger disabled={isLoading} asChild>
        <Button
          isLoading={isLoading}
          size="icon"
          variant="ghost"
          className={cn(
            'absolute top-1/2 right-0.5 -translate-y-1/2 z-20 hover:bg-secondary size-6 focus:outline-none',
            !isLoading &&
              'invisible group-hover:visible data-[state=open]:visible',
            isActive && 'visible',
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Icon as={EllipsisVertical} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        id="sidebar-chat-dropdown"
        className="min-w-[160px]"
        align="end"
      >
        {openRename && (
          <RenameForm
            defaultValues={{ name: chat.name ?? '' }}
            updateName={(name: string) => {
              updateName({
                id: chat.id,
                name: name,
                type: 'CHAT',
              });
            }}
            onSubmit={() => {
              setOpenMenu(false);
            }}
          />
        )}

        {!openRename && (
          <>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setOpenRename(true);
              }}
            >
              <Icon size="sm" as={SquarePen} />
              Rename
            </DropdownMenuItem>

            <DropdownMenuItem onSelect={handleDeleteClick}>
              <Icon size="sm" as={TrashIcon} />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SidebarChatDropdown;
